import React, { useState, useEffect } from 'react';
import JSONCompareViewer from '../../../Components/JSONCompareViewer';
import InputField from '../../../Components/InputField';
import TextAreaField from '../../../Components/TextAreaField';
import ToastManager from '../../../Components/ToastManager';
import { NetworkManager } from '../../../Components/NetworkManager';

interface InvoiceDetailViewerProps {
    groupDetails: any;
    groupCode: string;
    onRefresh: (groupCode: string) => void;
    onViewHistory: (activeTab: string, id: any) => void;
    onClose?: () => void;
}

const InvoiceDetailViewer: React.FC<InvoiceDetailViewerProps> = ({
    groupDetails,
    groupCode,
    onRefresh,
    onViewHistory,
    onClose
}) => {
    const [activeTab, setActiveTab] = useState('');
    const [itemComments, setItemComments] = useState<any>({});
    const [itemPayloads, setItemPayloads] = useState<any>({});

    useEffect(() => {
        if (groupDetails) {
            if (groupDetails.invoices?.length > 0) setActiveTab('invoice');
            else if (groupDetails.statements?.length > 0) setActiveTab('statement');
            else if (groupDetails.payments?.length > 0) setActiveTab('payment');
        }
    }, [groupDetails]);

    const handlePayloadChange = (id: any, newData: any) => {
        setItemPayloads((prev: any) => ({ ...prev, [id]: newData }));
    };

    const handleCommentChange = (id: any, value: any) => {
        setItemComments((prev: any) => ({ ...prev, [id]: value }));
    };

    const handleSave = (sendToPrextra: boolean) => {
        const items = activeTab === 'invoice' ? groupDetails?.invoices :
            activeTab === 'statement' ? groupDetails?.statements :
                groupDetails?.payments;

        if (!items) return;

        let processedCount = 0;
        let hasError = false;

        items.forEach((item: any) => {
            const itemId = item.id || item.invoiceId || item.documentId;
            const payload = {
                id: itemId,
                payload: itemPayloads[itemId] || item.payload,
                comment: itemComments[activeTab] || "",
                sendToPrextra: sendToPrextra
            };

            NetworkManager.sendJsonRequest('api/prextra/review/save', payload, '', 'POST', true, (res: any) => {
                processedCount++;
                if (res) {
                    const response = JSON.parse(res);
                    if (!response.errorMessage) {
                            onRefresh(groupCode);
                    } else {
                        hasError = true;
                        ToastManager.showToast(response.errorMessage, 'Error');
                    }
                }

                if (processedCount === items.length) {
                    if (!hasError) {
                        ToastManager.showToast("Saved Successfully", 'Success');
                        
                        const checkCanClose = () => {
                            const invoicesValid = groupDetails?.invoices?.length > 0 
                                ? (activeTab === 'invoice' ? sendToPrextra : groupDetails.invoices[0].sentToPrextra)
                                : true;
                            
                            const statementsValid = groupDetails?.statements?.length > 0
                                ? (activeTab === 'statement' ? sendToPrextra : groupDetails.statements[0].sentToPrextra)
                                : true;

                            const paymentsValid = groupDetails?.payments?.length > 0
                                ? (activeTab === 'payment' ? sendToPrextra : groupDetails.payments[0].sentToPrextra)
                                : true;

                            return invoicesValid && statementsValid && paymentsValid;
                        };

                        if (sendToPrextra && checkCanClose() && onClose) {
                            onClose();
                        } else {
                            onRefresh(groupCode);
                        }
                    }
                }
            }, (err: any) => {
                processedCount++;
                console.error(err);
                hasError = true;
                if (processedCount === items.length) {
                    ToastManager.showToast("Error occurred while saving", 'Error');
                }
            });
        });
    };

    const handleViewHistory = () => {
        let id = null;
        if (activeTab === 'invoice' && groupDetails?.invoices && groupDetails.invoices.length > 0) {
            id = groupDetails.invoices[0].id;
        } else if (activeTab === 'statement' && groupDetails?.statements && groupDetails.statements.length > 0) {
            id = groupDetails.statements[0].id;
        } else if (activeTab === 'payment' && groupDetails?.payments && groupDetails.payments.length > 0) {
            id = groupDetails.payments[0].id;
        }

        if (!id) {
            ToastManager.showToast("No document found for this tab", 'Warning');
            return;
        }
        onViewHistory(activeTab, id);
    };

    const isSentToPrextra = (activeTab === 'invoice' ? groupDetails?.invoices?.[0]?.sentToPrextra :
        activeTab === 'statement' ? groupDetails?.statements?.[0]?.sentToPrextra :
        activeTab === 'payment' ? groupDetails?.payments?.[0]?.sentToPrextra : false);

    return (
        <div className="p-3 d-flex flex-column" style={{ height: 'calc(100vh - 70px)' }}>
            <div className="d-flex justify-content-between align-items-center border-bottom pb-2 mt-2">
                <ul className="nav nav-tabs border-bottom-0" style={{ marginBottom: "-1px" }}>
                    {groupDetails?.invoices?.length > 0 && (
                        <li className="nav-item" style={{ cursor: 'pointer' }}>
                            <a className={`nav-link ${activeTab === 'invoice' ? 'active' : ''}`} onClick={() => setActiveTab('invoice')} title={groupDetails?.invoices?.[0]?.isFlagged ? "Flagged Issues" : groupDetails?.invoices?.[0]?.sentToPrextra ? "Sent to Prextra" : "Pending"}>
                                <span title="Invoices">Invoices</span> {groupDetails?.invoices?.[0]?.isFlagged && <i className="fa-solid fa-circle-info text-danger ms-1"></i>}
                                {groupDetails?.invoices?.[0]?.sentToPrextra && <i className="fa-solid fa-circle-check text-success ms-1"></i>}
                                {!groupDetails?.invoices?.[0]?.isFlagged && !groupDetails?.invoices?.[0]?.sentToPrextra && <i className="fa-solid fa-circle-dot text-warning ms-1"></i>}
                            </a>
                        </li>
                    )}
                    {groupDetails?.statements?.length > 0 && (
                        <li className="nav-item" style={{ cursor: 'pointer' }}>
                            <a className={`nav-link ${activeTab === 'statement' ? 'active' : ''}`} onClick={() => setActiveTab('statement')} title={groupDetails?.statements?.[0]?.isFlagged ? "Flagged Issues" : groupDetails?.statements?.[0]?.sentToPrextra ? "Sent to Prextra" : "Pending"}>
                                <span title="Statement">Statement</span> {groupDetails?.statements?.[0]?.isFlagged && <i className="fa-solid fa-circle-info text-danger ms-1"></i>}
                                {groupDetails?.statements?.[0]?.sentToPrextra && <i className="fa-solid fa-circle-check text-success ms-1"></i>}
                                {!groupDetails?.statements?.[0]?.isFlagged && !groupDetails?.statements?.[0]?.sentToPrextra && <i className="fa-solid fa-circle-dot text-warning ms-1"></i>}
                            </a>
                        </li>
                    )}
                    {groupDetails?.payments?.length > 0 && (
                        <li className="nav-item" style={{ cursor: 'pointer' }}>
                            <a className={`nav-link ${activeTab === 'payment' ? 'active' : ''}`} onClick={() => setActiveTab('payment')} title={groupDetails?.payments?.[0]?.isFlagged ? "Flagged Issues" : groupDetails?.payments?.[0]?.sentToPrextra ? "Sent to Prextra" : "Pending"}>
                                <span title="Payment">Payment</span> {groupDetails?.payments?.[0]?.sentToPrextra && <i className="fa-solid fa-circle-check text-success ms-1"></i>}
                                {groupDetails?.payments?.[0]?.isFlagged && <i className="fa-solid fa-circle-info text-danger ms-1"></i>}
                                {!groupDetails?.payments?.[0]?.sentToPrextra && <i className="fa-solid fa-circle-dot text-warning ms-1"></i>}
                            </a>
                        </li>
                    )}
                </ul>
                <div className="d-flex align-items-center gap-2">
                    <button className="btn btn-outline-primary btn-sm" onClick={handleViewHistory}>View {activeTab} history</button>
                </div>
            </div>
            <div className="tab-content mt-3 flex-grow-1" style={{ overflowY: 'auto', overflowX: 'hidden', paddingRight: '5px' }}>
                {activeTab === 'invoice' && (
                    <div className="p-2">
                        {groupDetails?.invoices?.map((item: any, idx: number) => (
                            <div key={idx} className="mb-4 pb-4">
                                <div className="col-lg-12">
                                    <h6 className="mb-2">Invoice Payload - {item.invoiceCode}</h6>
                                    <JSONCompareViewer
                                        data={item.payload}
                                        title={`${item.invoiceCode}`}
                                        documentId={item.documentId || item.id || item.invoiceId}
                                        documentType="invoice"
                                        height="640px"
                                        onDataChange={(newData) => handlePayloadChange(item.id || item.invoiceId || item.documentId, newData)}
                                        allowAdd={false}
                                        allowDelete={false}
                                        allowCopy={true}
                                        flaggedComments={item.flaggedComments || []}
                                    />
                                </div>
                            </div>
                        ))}
                    </div>
                )}
                {activeTab === 'statement' && (
                    <div className="p-2">
                        {groupDetails?.statements?.map((item: any, idx: number) => (
                            <div key={idx} className="mb-4 pb-4">
                                <div className="row g-3">
                                    <div className="col-lg-12">
                                        <h6 className="mb-2">Statement Payload - {item.invoiceCode}</h6>
                                        <JSONCompareViewer
                                            data={item.payload}
                                            title={`${item.invoiceCode}`}
                                            documentId={item.documentId || item.id || item.invoiceId}
                                            documentType="statement"
                                            height="730px"
                                            onDataChange={(newData) => handlePayloadChange(item.id || item.invoiceId || item.documentId, newData)}
                                            allowAdd={false}
                                            allowDelete={false}
                                            allowCopy={true}
                                            flaggedComments={item.flaggedComments || []}
                                        />
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
                {activeTab === 'payment' && (
                    <div className="p-2">
                        {groupDetails?.payments?.map((item: any, idx: number) => (
                            <div key={idx} className="mb-4 pb-4">
                                <div className="row g-3">
                                    <div className="col-lg-12">
                                        <div className="d-flex justify-content-between align-items-center mb-2">
                                            <h6 className="mb-0 align-self-start mx-1">Payment Payload - {item.invoiceCode}</h6>
                                            {item.totalAmount && <div className="d-flex align-items-center gap-2">
                                                <span className="small text-muted">Amount:</span>
                                                <InputField type="text" className="form-control form-control-sm clsStandardText"
                                                    placeholder="Amount" disabled={true}
                                                    style={{ width: '120px' }}
                                                    value={item.totalAmount} />
                                            </div>}
                                        </div>
                                        <JSONCompareViewer
                                            data={item.payload}
                                            title={`${item.invoiceCode}`}
                                            documentId={item.documentId || item.id || item.invoiceId}
                                            documentType="payment"
                                            height="640px"
                                            onDataChange={(newData) => handlePayloadChange(item.id || item.invoiceId || item.documentId, newData)}
                                            allowAdd={false}
                                            allowDelete={false}
                                            allowCopy={true}
                                        />
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
            <div className="d-flex align-items-center gap-2 border-top pt-3 mt-2">
                <div className="flex-grow-1 mx-2 mt-2" style={{ width: '300px' }}>
                    <TextAreaField
                        style={{ height: '50px', cursor: isSentToPrextra ? 'not-allowed' : 'pointer' }}
                        value={itemComments[activeTab] || ''}
                        onChange={(e: any) => handleCommentChange(activeTab, e.target.value)}
                        placeholder="Add Comment"
                        disabled={isSentToPrextra}
                    />
                </div>
                <div className="d-flex gap-2 ms-auto me-2">
                    <button
                        className="btn btn-primary"
                        disabled={isSentToPrextra || Object.values(itemComments).some(
                            (c: any) => c && c.trim().length > 0 && c.trim().length <= 3
                        )}
                        onClick={() => handleSave(false)}
                        style={{ cursor: isSentToPrextra ? 'not-allowed' : 'pointer' }}
                    >
                        Save
                    </button>
                    <button
                        className="btn btn-success"
                        disabled={isSentToPrextra || (Object.values(itemComments).some(
                            (c: any) => c && c.trim().length > 0
                        ) || (groupDetails?.invoices?.[0]?.isFlagged || groupDetails?.statements?.[0]?.isFlagged))}
                        onClick={() => handleSave(true)}
                        style={{ cursor: isSentToPrextra ? 'not-allowed' : 'pointer' }}
                    >
                        Save & Send to Prextra
                    </button>
                </div>
            </div>
        </div>
    );
};

export default InvoiceDetailViewer;
